// @ts-nocheck
import { useEffect } from "react";
import { toast } from "sonner";
import socket from "../utilities/adminSocket";
import { connectIfIdle } from "../utilities/socket";
import { Storage } from "../utilities/setting";
import { ROUTES, USER_ROLES } from "../utilities/constant";
import { clearApiCallShared } from "../service/sharedGet";
import { DASHBOARD_REFRESH_EVENT } from "../features/cti2/useDashboardRefresh";

const RECONNECT_REFRESH_JITTER_MS = 30000;
const ALL_DASHBOARD_SECTIONS = ["leads", "campaigns", "groups", "follow_ups"];

// Matches legacy cloudtelephony_frontend's useAdminBreakNotifications.js
// ADMIN_ROLES exactly — this set was missing RESELLER/COMPANY_ADMIN,
// so those roles never got admin break/follow-up notifications even
// once this hook is wired up.
const ADMIN_ROLES = new Set([
  USER_ROLES.SUPERADMIN,
  USER_ROLES.CALL_CENTRE_ADMIN,
  USER_ROLES.SUB_SUPERADMIN,
  USER_ROLES.CALL_CENTRE_TEAM_LEAD,
  USER_ROLES.RESELLER,
  USER_ROLES.COMPANY_ADMIN,
]);

const formatScheduledAt = (payload) => {
  const iso = payload?.scheduled_at;
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    }
  }
  const date = payload?.follow_up_date || "";
  const time = payload?.follow_up_time || "";
  return `${date} ${time}`.trim() || "scheduled time";
};

function formatExceededFor(payload) {
  if (!payload?.scheduled_end_at) return "";
  const scheduled = new Date(payload.scheduled_end_at).getTime();
  const now = Date.now();
  const secs = Math.max(0, Math.floor((now - scheduled) / 1000));
  if (secs < 60) return `${secs}s over`;
  const mins = Math.floor(secs / 60);
  const rem = secs % 60;
  return rem ? `${mins}m ${rem}s over` : `${mins}m over`;
}

function useAdminBreakNotifications() {
  useEffect(() => {
    let user;
    try {
      user = JSON.parse(Storage.getItem("user") || "null");
    } catch (e) {
      return undefined;
    }

    if (!user?.id) return undefined;

    const isAdmin = ADMIN_ROLES.has(user?.role);
    const adminId = user.id;

    const refetchNotifications = () =>
      window.dispatchEvent(new CustomEvent("cti:notification_refresh", { detail: {} }));


    const handleConnect = () => {
      socket.emit("join_admin", { adminId });
      refetchNotifications();
    };

    const emitDashboardRefresh = (detail) => {
      if (detail?.sections?.includes("groups")) clearApiCallShared(); // agent_schedule is shared-cached 10s
      window.dispatchEvent(new CustomEvent(DASHBOARD_REFRESH_EVENT, { detail }));
    };

    const handleReconnect = () => {
      socket.emit("join_admin", { adminId });
      emitDashboardRefresh({ sections: ALL_DASHBOARD_SECTIONS, jitterMs: RECONNECT_REFRESH_JITTER_MS });
    };

    const handleDashboardRefresh = (payload) => {
      const sections = Array.isArray(payload?.sections) ? payload.sections : [];
      if (sections.length) emitDashboardRefresh({ sections });
    };

    const handleConnectError = (err) => {
      console.log("Admin socket connection error:", err?.message);
    };

    const handleBreakExceeded = (payload) => {
      const agentName = payload?.agent?.name || "Agent";
      const breakTitle = payload?.break?.title || "Break";
      const allowed = payload?.break?.allowed_duration_minutes;
      const overBy = formatExceededFor(payload);

      const message =
        `${agentName} has exceeded the "${breakTitle}" break` +
        (allowed ? ` (allowed ${allowed} min).` : "")

      toast.warning(message, {
        duration: 8000,
        id: `break-exceeded-${payload?.userBreakId || agentName}`,
      });
    };

    const handleFollowUpMissed = (payload) => {
      const agentName = payload?.agent?.name || "Agent";
      const contact = payload?.did_number || "a contact";
      const message =
        `${agentName} missed a follow-up with ${contact} scheduled at ${formatScheduledAt(
          payload
        )}.`;

      toast.warning(message, {
        duration: 10000,
        id: `follow-up-missed-${payload?.followUpId || agentName}`,
      });
    };

    const handleForceLogout = () => {
      try { window.__ctiReleaseAllMedia?.(); } catch { /* noop */ }
      Storage.clearAll();
      socket.disconnect();
      window.location.replace(`/${ROUTES.LOGIN}`);
    };

    const handleNotificationRefresh = (payload) => {
      window.dispatchEvent(
        new CustomEvent("cti:notification_refresh", { detail: payload || {} })
      );
    };

    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
    socket.io.on("reconnect", handleReconnect);
    if (isAdmin) {
      socket.on("break_exceeded", handleBreakExceeded);
      socket.on("follow_up_missed", handleFollowUpMissed);
    }
    socket.on("notification_refresh", handleNotificationRefresh);
    socket.on("force_logout", handleForceLogout);
    socket.on("dashboard_refresh", handleDashboardRefresh);

    if (!socket.connected) {
      connectIfIdle(socket);
    } else {
      socket.emit("join_admin", { adminId });
    }

    return () => {
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
      socket.io.off("reconnect", handleReconnect);
      socket.off("break_exceeded", handleBreakExceeded);
      socket.off("follow_up_missed", handleFollowUpMissed);
      socket.off("notification_refresh", handleNotificationRefresh);
      socket.off("force_logout", handleForceLogout);
      socket.off("dashboard_refresh", handleDashboardRefresh);
    };
  }, []);
}

export default useAdminBreakNotifications;
